/**
 * Список переменных блоками (как ConditionalPromptsList).
 * Каждый блок: ключ, тип, составные условия. Inline редактирование.
 */
import React, { useState, useEffect } from 'react'
import { useI18n } from '../i18n'
import { Select, SettingsSavePanel } from './ui'
import { DeleteButton } from './ui'
import ConditionBuilder, { L3_KEYS, L2_VAR_KEYS } from './ConditionBuilder'
import ConfirmModal from './ConfirmModal'

const VAR_TYPES = [
  { value: 'bool', labelKey: 'settings.variables.typeBool' },
  { value: 'enum', labelKey: 'settings.variables.typeEnum' },
]

function emptyCond(keys) {
  return { param: keys[0] || 'engagement_score', op: 'gte', value: 0 }
}

const VariableDefinitionsList = React.forwardRef(function VariableDefinitionsList({
  definitions,
  onSave,
  saving,
  level = 'l4',
  l3Keys = [],
  onDirtyChange,
  hidePanel,
}, ref) {
  const { t } = useI18n()
  const [local, setLocal] = useState(definitions)
  const [deleteVariableIdx, setDeleteVariableIdx] = useState(null)
  const [deleteCondition, setDeleteCondition] = useState(null) // { variableIdx, valueIdx }
  const [deleteCondGroupInfo, setDeleteCondGroupInfo] = useState(null) // { variableIdx, valueIdx, condGroupIdx }

  useEffect(() => {
    setLocal(definitions)
  }, [definitions])

  const paramKeys = level === 'l3'
    ? [...L3_KEYS, ...L2_VAR_KEYS]
    : [...L3_KEYS, ...L2_VAR_KEYS, ...l3Keys]

  function addVariable() {
    const def = {
      key: `var_${local.length + 1}`,
      type: 'bool',
      conditions: emptyCond(paramKeys),
    }
    setLocal([...local, def])
  }

  function updateVariable(idx, updates) {
    const next = [...local]
    next[idx] = { ...next[idx], ...updates }
    setLocal(next)
  }

  function removeVariable(idx) {
    setLocal(local.filter((_, i) => i !== idx))
    setDeleteVariableIdx(null)
  }

  function confirmRemoveVariable() {
    if (deleteVariableIdx != null) removeVariable(deleteVariableIdx)
  }

  const isDirty = JSON.stringify(local) !== JSON.stringify(definitions)

  useEffect(() => {
    if (onDirtyChange) onDirtyChange(isDirty)
  }, [isDirty, onDirtyChange])

  function handleSave() {
    const toSend = local.map((d) => {
      const out = { ...d }
      if (d.type === 'enum' && Array.isArray(d.value_entries)) {
        out.value_conditions = {}
        for (const e of d.value_entries) {
          if (e.value && e.conditions) {
            const key = e.value.trim()
            // conditions — массив условий (OR между ними)
            if (Array.isArray(e.conditions) && e.conditions.length === 1) {
              // Один condition — сохраняем как dict (backward compat)
              out.value_conditions[key] = e.conditions[0]
            } else if (Array.isArray(e.conditions) && e.conditions.length > 1) {
              // Несколько conditions — сохраняем как массив (OR)
              out.value_conditions[key] = e.conditions
            } else if (!Array.isArray(e.conditions)) {
              // Legacy: одиночный condition как dict
              out.value_conditions[key] = e.conditions
            }
          } else if (e.value && e.condition) {
            // Legacy compat
            out.value_conditions[e.value.trim()] = e.condition
          }
        }
        delete out.value_entries
      }
      return out
    })
    onSave(toSend)
  }

  React.useImperativeHandle(ref, () => ({
    save: handleSave
  }))

  /**
   * Раскрывает вложенный OR внутри AND-условия в отдельные OR-группы.
   * Пример: {and: [{or: [A, B]}, C]} → [{and: [A, C]}, {and: [B, C]}]
   * Это нужно, потому что UI отображает OR через "+ ИЛИ" группы,
   * а ConditionBuilder внутри группы работает только с AND (allowOr=false).
   * Без этого вложенный OR отображается как AND — баг.
   */
  function expandInnerOr(cond) {
    if (!cond || typeof cond !== 'object') return [cond]
    // Листовое условие — оставляем как есть
    if ('param' in cond && 'op' in cond) return [cond]

    const logic = (cond.logic || 'and').toLowerCase()
    const subs = cond.conditions || []

    if (logic === 'or') {
      // Верхнеуровневый OR — каждый элемент становится отдельной группой
      return subs.flatMap(s => expandInnerOr(s))
    }

    if (logic === 'and') {
      // Проверяем есть ли вложенный OR
      const hasInnerOr = subs.some(s => s && s.logic === 'or')
      if (!hasInnerOr) {
        // Чистый AND — одна группа
        return [cond]
      }

      // Дистрибутивный закон: (A OR B) AND C → [A AND C, B AND C]
      let groups = [[]]
      for (const s of subs) {
        if (s && s.logic === 'or' && Array.isArray(s.conditions)) {
          const newGroups = []
          for (const existing of groups) {
            for (const branch of s.conditions) {
              newGroups.push([...existing, branch])
            }
          }
          groups = newGroups
        } else {
          for (const g of groups) {
            g.push(s)
          }
        }
      }

      return groups.map(g =>
        g.length === 1 ? g[0] : { logic: 'and', conditions: g }
      )
    }

    return [cond]
  }

  /**
   * Конвертация value_conditions → value_entries с поддержкой массива условий.
   * value_conditions[key] может быть dict (одним условием) или array (OR группы).
   * Вложенные OR раскрываются в отдельные OR-группы через expandInnerOr.
   */
  const valueEntries = (def) => {
    if (Array.isArray(def.value_entries)) return def.value_entries
    const vc = def.value_conditions || {}
    return Object.entries(vc).map(([value, cond]) => {
      // cond может быть dict или array
      if (Array.isArray(cond)) {
        // Уже OR-группы, но каждая может содержать вложенный OR
        const expanded = cond.flatMap(c => expandInnerOr(c))
        return { value, conditions: expanded }
      }
      return { value, conditions: expandInnerOr(cond) }
    })
  }

  function addEnumValue(idx) {
    const def = local[idx]
    const entries = valueEntries(def)
    let name = 'value'
    let n = 1
    while (entries.some((e) => e.value === name + n)) n++
    name = name + n
    const newEntry = { value: name, conditions: [emptyCond(paramKeys)] }
    updateVariable(idx, {
      value_entries: [...entries, newEntry],
      value_conditions: undefined,
    })
  }

  function updateEnumValue(idx, valueIdx, updates) {
    const def = local[idx]
    const entries = [...valueEntries(def)]
    entries[valueIdx] = { ...entries[valueIdx], ...updates }
    updateVariable(idx, { value_entries: entries, value_conditions: undefined })
  }

  function removeEnumValue(idx, valueIdx) {
    const def = local[idx]
    const entries = valueEntries(def).filter((_, i) => i !== valueIdx)
    updateVariable(idx, { value_entries: entries, value_conditions: undefined })
    setDeleteCondition(null)
  }

  function confirmRemoveCondition() {
    if (deleteCondition) removeEnumValue(deleteCondition.variableIdx, deleteCondition.valueIdx)
  }

  /** Добавить OR-группу (ещё одно условие) для enum-значения */
  function addConditionGroup(varIdx, valueIdx) {
    const def = local[varIdx]
    const entries = [...valueEntries(def)]
    const entry = entries[valueIdx]
    const conds = Array.isArray(entry.conditions) ? [...entry.conditions] : [entry.conditions || emptyCond(paramKeys)]
    conds.push(emptyCond(paramKeys))
    entries[valueIdx] = { ...entry, conditions: conds }
    updateVariable(varIdx, { value_entries: entries, value_conditions: undefined })
  }

  /** Обновить конкретную OR-группу */
  function updateConditionGroup(varIdx, valueIdx, condGroupIdx, newCond) {
    const def = local[varIdx]
    const entries = [...valueEntries(def)]
    const entry = entries[valueIdx]
    const conds = Array.isArray(entry.conditions) ? [...entry.conditions] : [entry.conditions]
    conds[condGroupIdx] = newCond
    entries[valueIdx] = { ...entry, conditions: conds }
    updateVariable(varIdx, { value_entries: entries, value_conditions: undefined })
  }

  /** Удалить OR-группу */
  function removeConditionGroup(varIdx, valueIdx, condGroupIdx) {
    const def = local[varIdx]
    const entries = [...valueEntries(def)]
    const entry = entries[valueIdx]
    const conds = (Array.isArray(entry.conditions) ? [...entry.conditions] : [entry.conditions]).filter((_, i) => i !== condGroupIdx)
    if (conds.length === 0) conds.push(emptyCond(paramKeys))
    entries[valueIdx] = { ...entry, conditions: conds }
    updateVariable(varIdx, { value_entries: entries, value_conditions: undefined })
    setDeleteCondGroupInfo(null)
  }

  function confirmRemoveCondGroup() {
    if (deleteCondGroupInfo) {
      removeConditionGroup(deleteCondGroupInfo.variableIdx, deleteCondGroupInfo.valueIdx, deleteCondGroupInfo.condGroupIdx)
    }
  }

  return (
    <div className="variable-definitions-list variable-definitions-list--with-panel">
      <div className="variable-definitions-blocks">
        {local.map((def, idx) => (
          <div key={idx} className="variable-definition-block variable-level-l4">
            <div className="variable-block-header">
              <span className="variable-level-badge variable-level-badge--l4">L4</span>
              <input
                type="text"
                value={def.key || ''}
                onChange={(e) => updateVariable(idx, { key: e.target.value.trim() || def.key })}
                placeholder={t('settings.variables.keyPlaceholder')}
                className="variable-block-key"
              />
              {level === 'l4' && (
                <input
                  type="text"
                  value={def.name || ''}
                  onChange={(e) => updateVariable(idx, { name: e.target.value.trim() })}
                  placeholder={t('settings.variables.namePlaceholder')}
                  className="variable-block-name"
                  title={t('settings.variables.namePlaceholder')}
                />
              )}
              <Select
                options={VAR_TYPES.map((x) => ({ value: x.value, label: t(x.labelKey) || x.value }))}
                value={def.type || 'bool'}
                onChange={(v) => {
                  const prev = local[idx]
                  if (v === 'enum' && prev.type !== 'enum') {
                    const entries = valueEntries(prev)
                    if (entries.length === 0) {
                      updateVariable(idx, { type: v, value_entries: [{ value: 'value1', conditions: [emptyCond(paramKeys)] }], conditions: undefined })
                    } else {
                      updateVariable(idx, { type: v, conditions: undefined })
                    }
                  } else {
                    updateVariable(idx, { type: v })
                  }
                }}
                className="variable-block-type"
              />
              <DeleteButton
                onClick={() => setDeleteVariableIdx(idx)}
                title={t('common.delete')}
              />
            </div>

            {def.type === 'bool' && (
              <div className="variable-block-body">
                <div className="variable-condition-result-rows">
                  <div className="variable-condition-result-header">
                    <span className="variable-col-condition">{t('settings.variables.condition')}</span>
                    <span className="variable-col-result">{t('settings.variables.result')}</span>
                  </div>
                  <div className="variable-condition-result-row">
                    <div className="variable-col-condition">
                      <ConditionBuilder
                        condition={def.conditions || emptyCond(paramKeys)}
                        onChange={(c) => updateVariable(idx, { conditions: c })}
                        paramKeys={paramKeys}
                        confirmRemove
                      />
                    </div>
                    <div className="variable-col-result">
                      <Select
                        options={[
                          { value: true, label: t('settings.variables.resultTrue') },
                          { value: false, label: t('settings.variables.resultFalse') },
                        ]}
                        value={def.bool_result !== false}
                        onChange={(v) => updateVariable(idx, { bool_result: v })}
                        className="variable-result-select"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {def.type === 'enum' && (
              <div className="variable-block-body">
                <div className="variable-condition-result-rows">
                  <div className="variable-condition-result-header">
                    <span className="variable-col-condition">{t('settings.variables.condition')}</span>
                    <span className="variable-col-result">{t('settings.variables.result')}</span>
                  </div>
                  {valueEntries(def).map((entry, vIdx) => {
                    const condGroups = Array.isArray(entry.conditions) ? entry.conditions : [entry.conditions || emptyCond(paramKeys)]
                    return (
                      <div key={vIdx} className="variable-condition-result-row variable-condition-result-row--enum-multi">
                        <div className="variable-col-condition variable-col-condition--multi-groups">
                          {condGroups.map((cond, gIdx) => (
                            <React.Fragment key={gIdx}>
                              <div className="variable-condition-group">
                                <ConditionBuilder
                                  condition={cond || emptyCond(paramKeys)}
                                  onChange={(c) => updateConditionGroup(idx, vIdx, gIdx, c)}
                                  paramKeys={paramKeys}
                                  allowOr={false}
                                  confirmRemove
                                />
                                {condGroups.length > 1 && (
                                  <DeleteButton
                                    onClick={() => setDeleteCondGroupInfo({ variableIdx: idx, valueIdx: vIdx, condGroupIdx: gIdx })}
                                    title={t('common.delete')}
                                    className="condition-group-delete"
                                  />
                                )}
                              </div>
                              {gIdx < condGroups.length - 1 && (
                                <div className="condition-or-divider">
                                  <span className="condition-or-label">{t('settings.condition.or')}</span>
                                </div>
                              )}
                            </React.Fragment>
                          ))}
                          <button
                            type="button"
                            className="btn btn-ghost btn-xs condition-add-or-group"
                            onClick={() => addConditionGroup(idx, vIdx)}
                          >
                            + {t('settings.condition.or')}
                          </button>
                        </div>
                        <div className="variable-col-result">
                          <input
                            type="text"
                            value={entry.value || ''}
                            onChange={(e) => updateEnumValue(idx, vIdx, { value: e.target.value })}
                            placeholder={t('settings.variables.enumValuePlaceholder')}
                            className="variable-enum-value-input"
                          />
                        </div>
                        <DeleteButton
                          onClick={() => setDeleteCondition({ variableIdx: idx, valueIdx: vIdx })}
                          title={t('common.delete')}
                        />
                      </div>
                    )
                  })}
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => addEnumValue(idx)}
                  >
                    {t('settings.variables.addEnumValue')}
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
        <div className="variable-definitions-add">
          <button type="button" className="btn btn-secondary" onClick={addVariable}>
            {t('settings.variables.addBlock')}
          </button>
        </div>
      </div>

      {!hidePanel && (
        <SettingsSavePanel onSave={handleSave} saving={saving} dirty={isDirty} />
      )}

      <ConfirmModal
        open={deleteVariableIdx != null}
        title={t('settings.variables.deleteVariableTitle')}
        message={t('settings.variables.deleteVariableMessage', {
          key: local[deleteVariableIdx]?.key || '—',
        })}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        onConfirm={confirmRemoveVariable}
        onCancel={() => setDeleteVariableIdx(null)}
        danger
      />

      <ConfirmModal
        open={!!deleteCondition}
        title={t('settings.variables.deleteConditionTitle')}
        message={t('settings.variables.deleteConditionMessage', {
          value: deleteCondition
            ? (valueEntries(local[deleteCondition.variableIdx])?.[deleteCondition.valueIdx]?.value || '—')
            : '—',
        })}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        onConfirm={confirmRemoveCondition}
        onCancel={() => setDeleteCondition(null)}
        danger
      />

      <ConfirmModal
        open={!!deleteCondGroupInfo}
        title={t('settings.variables.deleteConditionTitle')}
        message={t('settings.variables.deleteConditionGroupMessage') || t('settings.variables.deleteConditionMessage', { value: '—' })}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        onConfirm={confirmRemoveCondGroup}
        onCancel={() => setDeleteCondGroupInfo(null)}
        danger
      />
    </div>
  )
})

export default VariableDefinitionsList
